import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatSessionRange } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DateField } from "@/components/DateField";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

import { clParts, clDate } from "@/lib/chileTime";

const p2 = (n) => String(n).padStart(2, "0");
const HOURS = Array.from({ length: 17 }, (_, i) => i + 6);
const MINUTES = ["00", "15", "30", "45"];

export function RescheduleSessionDialog({ session, durationMin, minDate, maxDate, onClose }) {
  const cur = clParts(session.starts_at);
  const [date, setDate] = useState(`${cur.y}-${p2(cur.m)}-${p2(cur.d)}`);
  const [hour, setHour] = useState(String(cur.h));
  const [minute, setMinute] = useState(MINUTES.includes(p2(cur.mi)) ? p2(cur.mi) : "00");
  const [busy, setBusy] = useState(false);
  const next = date ? clDate(date, hour, minute) : null;
  const save = async () => {
    if (!next) return;
    setBusy(true);
    try {
      await api.patch(`/business/sessions/${session.id}`, { starts_at: next.toISOString() });
      toast.success(`Sesión reprogramada: ${formatSessionRange(next, durationMin)}`);
      onClose(true);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-sm" data-testid="reschedule-dialog">
        <DialogHeader>
          <DialogTitle>Cambiar día y hora</DialogTitle>
          <DialogDescription>Actual: {formatSessionRange(session.starts_at, durationMin)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Día</Label><DateField min={minDate} max={maxDate} value={date} onChange={setDate} testId="reschedule-date" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Hora</Label>
              <Select value={hour} onValueChange={setHour}>
                <SelectTrigger data-testid="reschedule-hour"><SelectValue /></SelectTrigger>
                <SelectContent>{HOURS.map((h) => <SelectItem key={h} value={String(h)}>{p2(h)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Minutos</Label>
              <Select value={minute} onValueChange={setMinute}>
                <SelectTrigger data-testid="reschedule-minute"><SelectValue /></SelectTrigger>
                <SelectContent>{MINUTES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {next && <p className="rounded-lg bg-primary/10 text-primary text-sm font-medium px-3 py-2" data-testid="reschedule-preview">Nueva: {formatSessionRange(next, durationMin)}</p>}
        </div>
        <DialogFooter className="flex-row justify-end gap-2">
          <Button variant="outline" onClick={() => onClose(false)} data-testid="reschedule-cancel">Cancelar</Button>
          <Button onClick={save} disabled={busy || !next} data-testid="reschedule-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
